// ---- input ----
// A tap resolves on pointerdown, before any drag exists, so the two never fight.
// The drag is a ~20-line orbit rather than OrbitControls (an addon whose free
// pitch and zoom are exactly how a three-year-old loses the letters): yaw is
// free — the letters follow the camera round — while pitch is clamped and eases
// home.
//
// Two fingers pinch the camera in and out. It is clamped to a little closer
// than home and twice as far (ZOOM_IN/ZOOM_OUT, scene.ts) — close on Jiddo's
// face, or the whole field in one shot — and it eases home by itself after a
// while, like the pitch does, so a child who pinches all the way out and
// wanders off does not come back to a game the size of a stamp.
//
// The rules where the two gestures meet: the FIRST finger down taps and orbits,
// as it always did; a second finger ends the orbit where it stands (rather than
// letting the pinch slew the heading) and starts the pinch; and the finger left
// behind when a pinch ends does nothing until it lifts, so the view never jumps
// on the way out of one. Only a finger that is alone on the glass taps, so a
// pinch answers once, not twice.
import {unlockAudio} from './audio';
import {tap} from './round';
import {ZOOM_IN,ZOOM_OUT,view} from './scene';
import {$,clamp} from './util';

interface Drag{id:number;x0:number;y0:number;yaw0:number;el0:number;on:boolean}
interface Pinch{a:number;b:number;d0:number;z0:number}
// the gestures in progress, how many fingers are down, and when the last one
// left: the pitch comes home three seconds after, the zoom fifteen
export const pointer={drag:null as Drag|null,pinch:null as Pinch|null,down:0,idleT:-1e9};
const pts=new Map<number,{x:number;y:number}>();
const gap=(p:Pinch)=>{const a=pts.get(p.a)!,b=pts.get(p.b)!;return Math.hypot(a.x-b.x,a.y-b.y);};
// spread the fingers and the camera comes in: the gap grows, the radius shrinks
const zoomTo=(z:number)=>{view.zoom=clamp(z,ZOOM_IN,ZOOM_OUT);};
export function bindInput(){
  const cv=$<HTMLCanvasElement>('c');
  document.addEventListener('pointerdown',unlockAudio,{capture:true});
  cv.addEventListener('pointerdown',e=>{
    e.preventDefault();
    pts.set(e.pointerId,{x:e.clientX,y:e.clientY}); pointer.down=pts.size;
    // Capture is a nicety — it keeps a finger that slides off the canvas in the
    // gesture — and it throws on a pointer the browser does not think is down:
    // a synthetic one from the ?dbg harness, or a real one already gone. It
    // goes after the bookkeeping, and a throw does not cost the gesture.
    try{cv.setPointerCapture(e.pointerId);}catch(err){}
    if(pts.size===1){
      pointer.drag={id:e.pointerId,x0:e.clientX,y0:e.clientY,yaw0:view.yaw,el0:view.el,on:false};
      tap(e.clientX,e.clientY,performance.now());
    }else if(pts.size===2){
      pointer.drag=null;                       // the orbit stops where it stands
      const [a,b]=[...pts.keys()];
      pointer.pinch={a,b,d0:0,z0:view.zoom};
      pointer.pinch.d0=gap(pointer.pinch)||1;  // two fingers at one point would divide by nothing
    }
  });
  cv.addEventListener('pointermove',e=>{
    const p=pts.get(e.pointerId); if(!p)return;
    p.x=e.clientX; p.y=e.clientY;
    const z=pointer.pinch;
    if(z){zoomTo(z.z0*z.d0/Math.max(gap(z),1));return;}
    const d=pointer.drag; if(!d||d.id!==e.pointerId)return;
    const dx=e.clientX-d.x0, dy=e.clientY-d.y0;
    if(!d.on&&Math.hypot(dx,dy)<8)return;
    d.on=true; view.yaw=d.yaw0-dx*0.006; view.el=clamp(d.el0+dy*0.004,0.14,0.5);
  });
  const lift=(e:PointerEvent)=>{
    if(!pts.delete(e.pointerId))return;
    pointer.down=pts.size;
    const z=pointer.pinch;
    if(z&&(z.a===e.pointerId||z.b===e.pointerId))pointer.pinch=null;
    if(pointer.drag?.id===e.pointerId)pointer.drag=null;
    if(!pts.size)pointer.idleT=performance.now();
  };
  cv.addEventListener('pointerup',lift); cv.addEventListener('pointercancel',lift);
  // A trackpad pinch is not a touch at all — the browser sends it as a wheel
  // with ctrl held, and a plain wheel is the mouse's version of the same. Both
  // must be taken (preventDefault) or the ctrl one zooms the PAGE instead,
  // which the viewport tag stops a touch from doing but not this.
  cv.addEventListener('wheel',e=>{
    e.preventDefault();
    zoomTo(view.zoom*Math.exp(e.deltaY*(e.ctrlKey?0.01:0.0015)));
    pointer.idleT=performance.now();
  },{passive:false});
}
